import {
  Box,
  Checkbox,
  Chip,
  FormLabel,
  Grid,
  IconButton,
  Input,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs/ClosableDialog'
import { Button } from 'components/facets'
import { RichTextEditorWithGallery } from 'components/facets/RichTextEditorWithGallery'
import {
  ChangelogEntry,
  ChangelogReader,
  createChangelogEntry,
  deleteChangelogEntry,
  getAdminChangelog,
  getChangelogReaders,
  updateChangelogEntry,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'

const KNOWN_FEATURE_FLAGS = ['shopify', 'banned', 'updateFullOrders', 'cardtrader', 'taxation']

interface ChangelogFormState {
  title: string
  titleEs: string
  titleFr: string
  titleDe: string
  titleIt: string
  body: string
  bodyEs: string
  bodyFr: string
  bodyDe: string
  bodyIt: string
  date: string
  audienceType: string
  audienceUserIds: string
  audienceFeatureFlags: string[]
}

const emptyForm: ChangelogFormState = {
  title: '',
  titleEs: '',
  titleFr: '',
  titleDe: '',
  titleIt: '',
  body: '',
  bodyEs: '',
  bodyFr: '',
  bodyDe: '',
  bodyIt: '',
  date: new Date().toISOString().split('T')[0],
  audienceType: 'all_users',
  audienceUserIds: '',
  audienceFeatureFlags: [],
}

const LANGS = ['en', 'es', 'fr', 'de', 'it'] as const

const titleKey = (lang: string): keyof ChangelogFormState =>
  lang === 'en'
    ? 'title'
    : (`title${lang.charAt(0).toUpperCase() + lang.slice(1)}` as keyof ChangelogFormState)

const bodyKey = (lang: string): keyof ChangelogFormState =>
  lang === 'en'
    ? 'body'
    : (`body${lang.charAt(0).toUpperCase() + lang.slice(1)}` as keyof ChangelogFormState)

export const ChangelogManager = (): JSX.Element => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<ChangelogFormState>(emptyForm)
  const [formVisible, setFormVisible] = useState(true)
  const [langTab, setLangTab] = useState(0)
  const [editLangTab, setEditLangTab] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState<ChangelogFormState>(emptyForm)
  const [editSaving, setEditSaving] = useState(false)
  const [readersDialogOpen, setReadersDialogOpen] = useState(false)
  const [readers, setReaders] = useState<ChangelogReader[]>([])
  const [readersLoading, setReadersLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const fetchEntries = async () => {
    try {
      const data = await getAdminChangelog()
      setEntries(data)
    } catch {
      setEntries([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEntries()
  }, [])

  const isAudienceValid = (f: ChangelogFormState) => {
    if (f.audienceType === 'targeted') return f.audienceUserIds.trim().length > 0
    if (f.audienceType === 'featureFlags') return f.audienceFeatureFlags.length > 0
    return true
  }

  const isBodyEmpty = (html: string) => {
    const stripped = html.replace(/<[^>]*>/g, '').trim()
    return stripped.length === 0
  }

  const isFormValid = (f: ChangelogFormState) =>
    !!f.title &&
    !!f.titleEs &&
    !!f.titleFr &&
    !!f.titleDe &&
    !!f.titleIt &&
    !isBodyEmpty(f.body) &&
    !isBodyEmpty(f.bodyEs) &&
    !isBodyEmpty(f.bodyFr) &&
    !isBodyEmpty(f.bodyDe) &&
    !isBodyEmpty(f.bodyIt) &&
    !!f.date &&
    isAudienceValid(f)

  const buildPayload = (f: ChangelogFormState) => ({
    title: f.title,
    titleEs: f.titleEs,
    titleFr: f.titleFr,
    titleDe: f.titleDe,
    titleIt: f.titleIt,
    body: f.body,
    bodyEs: f.bodyEs,
    bodyFr: f.bodyFr,
    bodyDe: f.bodyDe,
    bodyIt: f.bodyIt,
    date: f.date,
    audienceType: f.audienceType,
    audienceUserIds:
      f.audienceType === 'targeted'
        ? f.audienceUserIds
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    audienceFeatureFlags: f.audienceType === 'featureFlags' ? f.audienceFeatureFlags : [],
  })

  const handleSubmit = async () => {
    if (!isFormValid(form)) return
    setSaving(true)
    try {
      await createChangelogEntry(buildPayload(form))
      enqueueSnackbar(t('changelog.createSuccess'), { variant: 'success' })
      setFormVisible(false)
      setForm(emptyForm)
      setLangTab(0)
      await fetchEntries()
      setFormVisible(true)
    } catch {
      enqueueSnackbar(t('changelog.createFailed'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (entry: ChangelogEntry) => {
    setEditingId(entry.id)
    setEditForm({
      title: entry.title,
      titleEs: entry.titleEs || '',
      titleFr: entry.titleFr || '',
      titleDe: entry.titleDe || '',
      titleIt: entry.titleIt || '',
      body: entry.body,
      bodyEs: entry.bodyEs || '',
      bodyFr: entry.bodyFr || '',
      bodyDe: entry.bodyDe || '',
      bodyIt: entry.bodyIt || '',
      date: entry.date,
      audienceType: entry.audienceType,
      audienceUserIds: entry.audienceUserIds?.join(', ') || '',
      audienceFeatureFlags: entry.audienceFeatureFlags || [],
    })
    setEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditingId(null)
    setEditForm(emptyForm)
    setEditLangTab(0)
  }

  const handleEditSubmit = async () => {
    if (!editingId || !isFormValid(editForm)) return
    setEditSaving(true)
    try {
      await updateChangelogEntry(editingId, {
        title: editForm.title,
        titleEs: editForm.titleEs,
        titleFr: editForm.titleFr,
        titleDe: editForm.titleDe,
        titleIt: editForm.titleIt,
        body: editForm.body,
        bodyEs: editForm.bodyEs,
        bodyFr: editForm.bodyFr,
        bodyDe: editForm.bodyDe,
        bodyIt: editForm.bodyIt,
        date: editForm.date,
        audienceType: editForm.audienceType,
        audienceUserIds:
          editForm.audienceType === 'targeted'
            ? editForm.audienceUserIds
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        audienceFeatureFlags:
          editForm.audienceType === 'featureFlags' ? editForm.audienceFeatureFlags : [],
      })
      enqueueSnackbar(t('changelog.updateSuccess'), { variant: 'success' })
      handleCloseEditDialog()
      await fetchEntries()
    } catch {
      enqueueSnackbar(t('changelog.updateFailed'), { variant: 'error' })
    } finally {
      setEditSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('changelog.deleteConfirm'))) return
    try {
      await deleteChangelogEntry(id)
      enqueueSnackbar(t('changelog.deleteSuccess'), { variant: 'success' })
      await fetchEntries()
    } catch {
      enqueueSnackbar(t('changelog.deleteFailed'), { variant: 'error' })
    }
  }

  const handleViewReaders = async (id: number) => {
    setReadersDialogOpen(true)
    setReadersLoading(true)
    try {
      const data = await getChangelogReaders(id)
      setReaders(data)
    } catch {
      enqueueSnackbar(t('changelog.loadReadersFailed'), { variant: 'error' })
      setReaders([])
    } finally {
      setReadersLoading(false)
    }
  }

  return (
    <div className="p-3">
      <h4 className="mb-3">{t('changelog.title')}</h4>

      <div className="my-5">
        <h6 className="m-0 p-0 mb-2">{t('changelog.newEntry')}</h6>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              label={t('changelog.date')}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label={t('changelog.audience')}
              size="small"
              fullWidth
              select
              value={form.audienceType}
              onChange={(e) => setForm((f) => ({ ...f, audienceType: e.target.value }))}
            >
              <MenuItem value="all_users">{t('changelog.allUsers')}</MenuItem>
              <MenuItem value="targeted">{t('changelog.specificUsers')}</MenuItem>
              <MenuItem value="featureFlags">{t('changelog.featureFlag')}</MenuItem>
            </TextField>
          </Grid>
          {form.audienceType === 'targeted' && (
            <Grid item xs={6} sm={4}>
              <TextField
                label={t('changelog.userIdsPlaceholder')}
                size="small"
                fullWidth
                value={form.audienceUserIds}
                onChange={(e) => setForm((f) => ({ ...f, audienceUserIds: e.target.value }))}
                placeholder="userId1, userId2"
              />
            </Grid>
          )}
          {form.audienceType === 'featureFlags' && (
            <Grid item xs={6} sm={4}>
              <FormLabel className="mb-1" style={{ fontSize: '0.75rem' }}>
                {t('changelog.featureFlags')}
              </FormLabel>
              <Select
                multiple
                fullWidth
                value={form.audienceFeatureFlags}
                onChange={(e) =>
                  setForm((f) => ({ ...f, audienceFeatureFlags: e.target.value as string[] }))
                }
                input={<Input />}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                    {(selected as string[]).map((flag) => (
                      <Chip key={flag} label={flag} size="small" />
                    ))}
                  </Box>
                )}
              >
                {KNOWN_FEATURE_FLAGS.map((flag) => (
                  <MenuItem key={flag} value={flag}>
                    <Checkbox checked={form.audienceFeatureFlags.includes(flag)} size="small" />
                    <ListItemText primary={flag} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
          <Grid item xs={12}>
            <ul className="nav nav-tabs">
              {LANGS.map((l, i) => (
                <li key={l} className="nav-item">
                  <a
                    className={`nav-link${langTab === i ? ' active' : ''}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setLangTab(i)
                    }}
                  >
                    {l.toUpperCase()}
                  </a>
                </li>
              ))}
            </ul>
            {formVisible && (
              <Box key={`create-${LANGS[langTab]}`} className="localized-content-form">
                <TextField
                  label={t('changelog.entryTitle')}
                  size="small"
                  fullWidth
                  value={form[titleKey(LANGS[langTab])] as string}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, [titleKey(LANGS[langTab])]: e.target.value }))
                  }
                />
                <Box mt={2}>
                  <FormLabel className="mb-3">{t('changelog.body')}</FormLabel>
                  <RichTextEditorWithGallery
                    value={form[bodyKey(LANGS[langTab])] as string}
                    onChange={(value) =>
                      setForm((f) => ({ ...f, [bodyKey(LANGS[langTab])]: value }))
                    }
                    placeholder={t('changelog.body')}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSubmit}
              disabled={saving || !isFormValid(form)}
              className="me-2"
            >
              {saving ? t('changelog.saving') : t('changelog.create')}
            </Button>
          </Grid>
        </Grid>
      </div>

      {loading ? (
        <span className="small text-muted">{t('common.loading')}</span>
      ) : entries.length === 0 ? (
        <span className="small text-muted">{t('changelog.noEntries')}</span>
      ) : (
        <div style={{ height: Math.min(entries.length * 45 + 40 + 30, 450) }}>
          <span className="mb-1">{t('changelog.entriesCount', { count: entries.length })}</span>
          <AutoSizer>
            {({ width }) => (
              <Table
                width={width}
                height={400}
                headerHeight={40}
                rowHeight={45}
                rowCount={entries.length}
                rowGetter={({ index }) => entries[index]}
              >
                <Column
                  width={width * 0.12}
                  label={t('changelog.date')}
                  dataKey="date"
                  cellRenderer={({ rowData }) => <span>{rowData.date}</span>}
                />
                <Column
                  width={width * 0.45}
                  label={t('changelog.entryTitle')}
                  dataKey="title"
                  cellRenderer={({ rowData }) => (
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      }}
                    >
                      {rowData.title}
                    </span>
                  )}
                />
                <Column
                  width={width * 0.25}
                  label={t('changelog.audience')}
                  dataKey="audienceType"
                  cellRenderer={({ rowData }) => (
                    <span>
                      {rowData.audienceType === 'all_users'
                        ? t('changelog.allUsers')
                        : rowData.audienceType === 'featureFlags'
                        ? `Flags: ${(rowData.audienceFeatureFlags || []).join(', ')}`
                        : `${rowData.audienceUserIds?.length || 0} users`}
                    </span>
                  )}
                />
                <Column
                  width={width * 0.18}
                  label={t('changelog.actions')}
                  dataKey="id"
                  disableSort
                  cellRenderer={({ rowData }) => (
                    <>
                      <IconButton size="small" onClick={() => handleViewReaders(rowData.id)}>
                        <i className="fa-light fa-eye" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(rowData)}>
                        <i className="fa-light fa-pen-to-square" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(rowData.id)}>
                        <i className="fa-light fa-trash" />
                      </IconButton>
                    </>
                  )}
                />
              </Table>
            )}
          </AutoSizer>
        </div>
      )}

      <ClosableDialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="lg" fullWidth>
        <h3 className="mb-3">{t('changelog.editEntry')}</h3>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <TextField
              label={t('changelog.date')}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editForm.date}
              onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label={t('changelog.audience')}
              size="small"
              fullWidth
              select
              value={editForm.audienceType}
              onChange={(e) => setEditForm((f) => ({ ...f, audienceType: e.target.value }))}
            >
              <MenuItem value="all_users">{t('changelog.allUsers')}</MenuItem>
              <MenuItem value="targeted">{t('changelog.specificUsers')}</MenuItem>
              <MenuItem value="featureFlags">{t('changelog.featureFlag')}</MenuItem>
            </TextField>
          </Grid>
          {editForm.audienceType === 'targeted' && (
            <Grid item xs={12}>
              <TextField
                label={t('changelog.userIdsPlaceholder')}
                size="small"
                fullWidth
                value={editForm.audienceUserIds}
                onChange={(e) => setEditForm((f) => ({ ...f, audienceUserIds: e.target.value }))}
                placeholder="userId1, userId2"
              />
            </Grid>
          )}
          {editForm.audienceType === 'featureFlags' && (
            <Grid item xs={6} sm={4}>
              <FormLabel className="mb-1" style={{ fontSize: '0.75rem' }}>
                {t('changelog.featureFlags')}
              </FormLabel>
              <Select
                multiple
                fullWidth
                value={editForm.audienceFeatureFlags}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    audienceFeatureFlags: e.target.value as string[],
                  }))
                }
                input={<Input />}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                    {(selected as string[]).map((flag) => (
                      <Chip key={flag} label={flag} size="small" />
                    ))}
                  </Box>
                )}
              >
                {KNOWN_FEATURE_FLAGS.map((flag) => (
                  <MenuItem key={flag} value={flag}>
                    <Checkbox checked={editForm.audienceFeatureFlags.includes(flag)} size="small" />
                    <ListItemText primary={flag} />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
          <Grid item xs={12}>
            <ul className="nav nav-tabs">
              {LANGS.map((l, i) => (
                <li key={l} className="nav-item">
                  <a
                    className={`nav-link${editLangTab === i ? ' active' : ''}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setEditLangTab(i)
                    }}
                  >
                    {l.toUpperCase()}
                  </a>
                </li>
              ))}
            </ul>
            <Box key={`edit-${LANGS[editLangTab]}`} className="localized-content-form">
              <TextField
                label={t('changelog.entryTitle')}
                size="small"
                fullWidth
                value={editForm[titleKey(LANGS[editLangTab])] as string}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    [titleKey(LANGS[editLangTab])]: e.target.value,
                  }))
                }
              />
              <Box mt={2}>
                <FormLabel className="mb-3">{t('changelog.body')}</FormLabel>
                <RichTextEditorWithGallery
                  value={editForm[bodyKey(LANGS[editLangTab])] as string}
                  onChange={(value) =>
                    setEditForm((f) => ({ ...f, [bodyKey(LANGS[editLangTab])]: value }))
                  }
                  placeholder={t('changelog.body')}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={handleCloseEditDialog} className="mr-2">
            {t('common.cancel')}
          </Button>
          <Button
            className="ms-2"
            variant="contained"
            color="primary"
            size="small"
            onClick={handleEditSubmit}
            disabled={editSaving || !isFormValid(editForm)}
          >
            {editSaving ? t('changelog.saving') : t('common.save')}
          </Button>
        </Box>
      </ClosableDialog>

      <ClosableDialog
        open={readersDialogOpen}
        onClose={() => setReadersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <h3 className="mb-3">{t('changelog.viewedBy')}</h3>
        {readersLoading ? (
          <span className="text-muted">{t('common.loading')}</span>
        ) : readers.length === 0 ? (
          <span className="text-muted">{t('changelog.noReaders')}</span>
        ) : (
          <>
            <span className="text-muted mb-2 d-block">
              {t('changelog.readersCount', { count: readers.length })}
            </span>
            <div style={{ maxHeight: 300, overflow: 'auto' }}>
              {readers.map((reader) => (
                <div
                  key={reader.userId}
                  className="d-flex justify-content-between align-items-center py-1 border-bottom"
                >
                  <span style={{ fontFamily: 'monospace' }}>{reader.userId}</span>
                  <span>{reader.email || '—'}</span>
                  <span className="fw-semibold">{reader.cardmarketUsername || '—'}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </ClosableDialog>
    </div>
  )
}
